// frontend/src/utils/logger.js
import { v4 as uuidv4 } from 'uuid';

const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
};

class Logger {
  constructor() {
    this.sessionId = uuidv4();
    this.userId = null;
  }

  setUser(user) {
    this.userId = user?.id || null;
    this.log('User session started', { userId: this.userId }, LOG_LEVELS.INFO);
  }

  clearUser() {
    this.log('User session ended', { userId: this.userId }, LOG_LEVELS.INFO);
    this.userId = null;
  }

  formatMessage(level, message, data = {}) {
    const timestamp = new Date().toISOString();
    return {
      timestamp,
      level,
      sessionId: this.sessionId,
      userId: this.userId,
      message,
      ...data
    };
  }

  log(message, data = {}, level = LOG_LEVELS.INFO) {
    const logEntry = this.formatMessage(level, message, data);
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      const { timestamp, sessionId, userId, ...rest } = logEntry;
      const logMethod = console[level] || console.log;
      logMethod(`[${timestamp}] [${level.toUpperCase()}]`, message, rest);
    }
    
    // In production, you might want to send logs to a logging service
    // this.sendToLoggingService(logEntry);
    
    return logEntry;
  }

  debug(message, data) {
    return this.log(message, data, LOG_LEVELS.DEBUG);
  }

  info(message, data) {
    return this.log(message, data, LOG_LEVELS.INFO);
  }

  warn(message, data) {
    return this.log(message, data, LOG_LEVELS.WARN);
  }

  error(message, error, data = {}) {
    const errorData = {
      ...data,
      error: {
        message: error?.message,
        stack: error?.stack,
        ...(error?.response?.data && { response: error.response.data })
      }
    };
    return this.log(message, errorData, LOG_LEVELS.ERROR);
  }
}

// Create a singleton instance
export const logger = new Logger();

export const logApiRequest = (config) => {
  const { method, url, params, data } = config;
  logger.debug(`API Request: ${method?.toUpperCase()} ${url}`, {
    method,
    url,
    params,
    data
  });
  return config;
};

export const logApiResponse = (response) => {
  const { config, status, statusText } = response;
  logger.debug(`API Response: ${config.method?.toUpperCase()} ${config.url} ${status}`, {
    status,
    statusText,
    url: config.url,
    method: config.method,
    response: response.data
  });
  return response;
};

export const logApiError = (error) => {
  if (error.response) {
    // Server responded with a status code outside 2xx
    const { status, statusText, data } = error.response;
    logger.error(
      `API Error: ${error.config.method?.toUpperCase()} ${error.config.url} ${status}`,
      error,
      {
        status,
        statusText,
        url: error.config.url,
        method: error.config.method,
        response: data
      }
    );
  } else if (error.request) {
    // Request was made but no response received
    logger.error(
      `API Network Error: ${error.config.method?.toUpperCase()} ${error.config.url}`,
      error,
      {
        message: 'No response received from server',
        url: error.config.url,
        method: error.config.method
      }
    );
  } else {
    // Something happened in setting up the request
    logger.error('API Request Setup Error', error, {
      message: error.message,
      url: error.config?.url,
      method: error.config?.method
    });
  }
  return Promise.reject(error);
};
